import { useCallback, useRef, useState } from 'react';
import type { ImpactoCascataResumo } from '@triade/contracts';
import { ImpactoCascataDialogo } from './impacto-cascata-dialogo';

interface Pendente {
  impacto: ImpactoCascataResumo;
  decidir: (confirmou: boolean) => void;
}

/**
 * Passo "mostrar o impacto antes de gravar". `confirmar(previa)` roda a previa (a API, sem gravar) e abre o dialogo;
 * a tela so grava se a promise resolver `true`:
 * - erro da previa sai como excecao (a tela trata como sempre, sem dialogo);
 * - Cancelar / Esc resolvem `false` (nada e gravado; a tela segue como estava);
 * - Confirmar resolve `true`.
 * Uma previa por vez: um segundo clique enquanto a previa ou o dialogo estao abertos resolve `false`.
 * Renderize `dialogo` na arvore da tela (ele so aparece com uma previa pendente).
 */
export function useConfirmarImpacto() {
  const [pendente, setPendente] = useState<Pendente | null>(null);
  const emCurso = useRef(false);

  const confirmar = useCallback(async (previa: () => Promise<ImpactoCascataResumo>): Promise<boolean> => {
    if (emCurso.current) return false;
    emCurso.current = true;
    try {
      const impacto = await previa();
      return await new Promise<boolean>((resolve) => {
        setPendente({
          impacto,
          decidir: (confirmou) => {
            setPendente(null);
            resolve(confirmou);
          },
        });
      });
    } finally {
      emCurso.current = false;
    }
  }, []);

  const dialogo = pendente ? (
    <ImpactoCascataDialogo impacto={pendente.impacto} onCancelar={() => pendente.decidir(false)} onConfirmar={() => pendente.decidir(true)} />
  ) : null;

  return { confirmar, dialogo };
}
